/**
 * Dental Pro Trial Checklist — Interactive page
 *
 * Premium check-list with persistent state (localStorage), progress bar,
 * grouped tasks, and a CTA to download the printable PDF version.
 *
 * Route: /dashboard/dental/trial-checklist
 */
import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import {
  CheckCircle2, Circle, Download, RotateCcw, Sparkles, Calendar, Users, FileText,
  Stethoscope, FileSignature, Activity, Megaphone, BarChart3, Smartphone, Trophy,
} from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../../App";

const STORAGE_KEY = "smartbooker.dental.trialChecklist.v1";

const PDF_URL = "/api/downloads/dental-pro-trial-checklist.pdf";

/** Sections — synced with the PDF template (12 sections, ~40 tasks). */
const SECTIONS = [
  {
    id: "s1", day: "Giorno 1", icon: Users,
    title: "Setup studio · Anagrafica e Team",
    color: "from-blue-500 to-cyan-500",
    tasks: [
      ["t1", "Compila l'anagrafica studio",                "Nome, indirizzo, telefono, P.IVA, logo · base per fatture e SmartPage."],
      ["t2", "Aggiungi i membri del team",                 "Dentisti, igienisti, segreteria · ruoli e colori distintivi."],
      ["t3", "Configura i servizi clinici",                "Visita, igiene, otturazione, devital., implanto... con durata e prezzo."],
      ["t4", "Imposta orari, pause e chiusure",            "Calendario settimanale + pause + ferie programmate."],
      ["t5", "Assegna i servizi al team (mapping)",        "Chi esegue cosa · attiva il filtro intelligente nel booking pubblico.", "pro"],
    ],
  },
  {
    id: "s2", day: "Giorno 2", icon: Calendar,
    title: "Smart Page pubblica · Booking online",
    color: "from-violet-500 to-fuchsia-500",
    tasks: [
      ["t1", "Personalizza la SmartPage white-label",      "Copertina, descrizione, contatti, mappa · è il tuo mini-sito."],
      ["t2", "Apri il booking pubblico da paziente",       "Prenota un test da incognito · verifica la UX end-to-end."],
      ["t3", "Configura reminder automatici WhatsApp/SMS", "24h + 12h + 2h prima · zero no-show, zero telefonate."],
      ["t4", "Pubblica il link sui social/sito",           "Genera QR code stampabile per la sala d'attesa."],
    ],
  },
  {
    id: "s3", day: "Giorni 3–4", icon: Stethoscope,
    title: "Smart Odontogram™ · 32 denti FDI",
    color: "from-cyan-500 to-emerald-500",
    tasks: [
      ["t1", "Crea un paziente di test",                   "Anagrafica + consensi GDPR (privacy/marketing/WhatsApp)."],
      ["t2", "Apri l'odontogramma interattivo SVG",        "32 denti FDI · zero canvas, zero immagini, retina-ready."],
      ["t3", "Prova gli 8 stati clinici",                  "Sano · carie · otturazione · corona · ponte · implanto · devital. · estratto."],
      ["t4", "Aggiungi annotazioni e cronologia",          "Trattamento + note tecniche + audit log automatico (30 eventi)."],
      ["t5", "Esamina la history per singolo dente",       "Tutti i trattamenti pregressi · operatore + data."],
    ],
  },
  {
    id: "s4", day: "Giorno 5", icon: FileText,
    title: "Smart Patient Intake™ · OCR Tessera Sanitaria",
    color: "from-emerald-500 to-teal-500",
    tasks: [
      ["t1", "Apri il modulo 'Scansiona Documento'",       "Bottone in alto a destra in pagina Clienti."],
      ["t2", "Scatta o carica foto di una tessera",        "Webcam back-camera o file picker JPEG/PNG/HEIC."],
      ["t3", "Verifica l'OCR Claude Vision + CF",          "Nome, cognome, data nascita, sesso + validazione CF.", "ai"],
      ["t4", "Controlla l'alert duplicati",                "Match automatico su CF/email/telefono · no duplicati."],
      ["t5", "Conferma e crea il cliente",                 "Anagrafica popolata in secondi · timeline + audit auto."],
    ],
  },
  {
    id: "s5", day: "Giorni 6–7", icon: FileText,
    title: "Smart Prescription™ · SSN-ready",
    color: "from-amber-500 to-orange-500",
    tasks: [
      ["t1", "Crea una nuova prescrizione",                "Counter atomico rx_number · paziente, farmaco, posologia."],
      ["t2", "Cambia stato (draft → sent → fulfilled)",    "Audit log + timeline cliente per tracciabilità."],
      ["t3", "Apri il dashboard Hub prescrizioni",         "Stato globale · filtri · contatori live."],
      ["t4", "Esporta XML SSN (Sistema TS)",               "Pronto per Agenzia Entrate / Sistema Tessera Sanitaria."],
      ["t5", "Prova il 'Monthly Flow Package'",            "Genera in 1 click tutte le prescrizioni ricorrenti del mese."],
    ],
  },
  {
    id: "s6", day: "Giorno 7", icon: FileSignature,
    title: "Smart Consents™ · GDPR & Firma",
    color: "from-rose-500 to-pink-500",
    tasks: [
      ["t1", "Genera un consenso informato",               "Template pre-configurati (anestesia, devital., implantologia)."],
      ["t2", "Raccogli la firma digitale del paziente",    "Tablet/smartphone · firma touch + archiviazione PDF auto."],
      ["t3", "Verifica i 3 toggle GDPR",                   "Privacy obbligatorio · Marketing opt-in · WhatsApp opt-in."],
    ],
  },
  {
    id: "s7", day: "Giorni 8–9", icon: Calendar,
    title: "Advanced Calendar · Team Columns + 4 Viste",
    color: "from-indigo-500 to-violet-500",
    tasks: [
      ["t1", "Esplora le 4 viste calendario",              "Team · Settimana · Agenda · Compatta · scenari diversi.", "pro"],
      ["t2", "Drag & drop appuntamento tra operatori",      "Riassegna con un gesto · optimistic update + rollback."],
      ["t3", "Smart Booking Filter per servizio",          "Filtra colonne per servizio: vedi solo staff abilitati."],
      ["t4", "Test conflict detection",                    "Bordo rosa + warning su appuntamenti sovrapposti."],
    ],
  },
  {
    id: "s8", day: "Giorni 10–11", icon: Activity,
    title: "CRM 360° · Smart Records AI",
    color: "from-violet-500 to-purple-500",
    tasks: [
      ["t1", "Apri un profilo paziente premium",           "Layout 3 colonne stile Stripe/Notion · hero card + 5 tab."],
      ["t2", "Esplora la timeline cronologica",            "Eventi (appt, prescr., consensi, doc) paginati cursor-based."],
      ["t3", "Carica un documento",                        "Drag&drop · base64 max 5MB · auto-classificato."],
      ["t4", "Genera il Riassunto AI Claude Sonnet 4.5",   "Summary clinico · alert · suggerimenti recall/igiene.", "ai"],
      ["t5", "Aggiungi note rapide e tag",                 "Quick-add sidebar · operatore assegnato + workflow."],
    ],
  },
  {
    id: "s9", day: "Giorno 12", icon: Megaphone,
    title: "WhatsApp Automation & Campagne",
    color: "from-emerald-500 to-green-500",
    tasks: [
      ["t1", "Configura template messaggi automatici",     "Reminder · conferma · post-visita · auguri · variabili dinamiche."],
      ["t2", "Crea una campagna recall pazienti dormienti","Filtri segmento + broadcast WhatsApp."],
      ["t3", "Tracciare aperture e click",                 "CTR · conversion rate · ROI per campagna."],
    ],
  },
  {
    id: "s10", day: "Giorno 13", icon: BarChart3,
    title: "LTV Analytics · Decisioni data-driven",
    color: "from-fuchsia-500 to-rose-500",
    tasks: [
      ["t1", "Apri il dashboard LTV",                      "Top 10 pazienti · trend mensile · retention rate."],
      ["t2", "Segmenta pazienti per fascia di spesa",      "Premium / Standard / Occasional · actionable per campagne."],
      ["t3", "Verifica i KPI revenue/conversion",          "Fatturato · no-show rate · revenue per operatore."],
    ],
  },
  {
    id: "s11", day: "Giorno 14 (AM)", icon: Smartphone,
    title: "Mobile, PWA & Sicurezza GDPR",
    color: "from-slate-600 to-slate-800",
    tasks: [
      ["t1", "Installa la PWA su smartphone (admin)",      "'Aggiungi a Home' · app-like, offline-ready."],
      ["t2", "Testa il calendario da mobile",              "Naviga, crea, conferma da telefono · responsive."],
      ["t3", "Verifica gli audit log GDPR",                "Tracciabilità di chi ha fatto cosa · esportabile."],
      ["t4", "Test esportazione dati (oblio)",             "Export ZIP completo o cancellazione GDPR-compliant."],
    ],
  },
  {
    id: "s12", day: "Giorno 14 (PM)", icon: Trophy,
    title: "Valutazione finale & ROI",
    color: "from-amber-500 to-yellow-500",
    tasks: [
      ["t1", "Calcola il ROI per il tuo studio",           "Tempo segreteria + recupero no-show + booking online extra."],
      ["t2", "Coinvolgi il team nelle decisioni",          "Demo da 15 min al personale · raccogli feedback."],
      ["t3", "Confronta con il software attuale",          "Funzioni · costo annuo · supporto · scalabilità multi-sede."],
      ["t4", "Attiva l'abbonamento Dental Pro",            "€179/mese o €1.790/anno (2 mesi gratis) · upgrade in 1 click."],
    ],
  },
];

const TOTAL_TASKS = SECTIONS.reduce((acc, s) => acc + s.tasks.length, 0);

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

const DentalProTrialChecklist = () => {
  const { language } = useLanguage();
  const [checked, setChecked] = useState(loadState);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(checked)); } catch { /* noop */ }
  }, [checked]);

  const completedCount = useMemo(
    () => Object.values(checked).filter(Boolean).length,
    [checked]
  );
  const progressPct = Math.round((completedCount / TOTAL_TASKS) * 100);

  const toggle = (sectionId, taskId) => {
    const key = `${sectionId}.${taskId}`;
    setChecked(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const resetAll = () => {
    if (window.confirm(language === "it"
      ? "Vuoi azzerare tutti i task spuntati?"
      : "Reset all checked tasks?")) {
      setChecked({});
      toast.success(language === "it" ? "Check-list azzerata" : "Checklist reset");
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="dental-pro-trial-checklist">
        {/* HERO */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white p-6 sm:p-8 shadow-2xl">
          <div className="absolute inset-0 opacity-30 pointer-events-none"
               style={{ background: "radial-gradient(circle at 15% 20%, rgba(99,102,241,.45) 0%, transparent 40%), radial-gradient(circle at 85% 80%, rgba(6,182,212,.35) 0%, transparent 40%)" }} />
          <div className="relative">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em]">
              <Sparkles className="w-3 h-3" /> Dental Pro · Trial Kit
            </div>
            <h1 className="text-2xl sm:text-3xl font-black leading-tight mt-3 tracking-tight">
              Check-list <span className="text-cyan-300">Trial 14 giorni</span>
            </h1>
            <p className="text-sm text-white/80 mt-2 max-w-2xl">
              {language === "it"
                ? "40 task in 6 aree per testare al massimo SmartBooker Dental Pro. Spunta mentre procedi: il tuo progresso resta salvato anche se chiudi la pagina."
                : "40 tasks across 6 areas to fully test SmartBooker Dental Pro. Check as you go: progress is saved locally."}
            </p>

            {/* Progress bar */}
            <div className="mt-5 flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px] max-w-md">
                <div className="flex items-center justify-between text-[11px] font-bold mb-1.5 text-white/90">
                  <span>{completedCount} / {TOTAL_TASKS} {language === "it" ? "completati" : "completed"}</span>
                  <span className="text-cyan-300">{progressPct}%</span>
                </div>
                <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-cyan-400 to-emerald-400 transition-all duration-500"
                       style={{ width: `${progressPct}%` }}
                       data-testid="trial-checklist-progress" />
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <a
                  href={PDF_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  download="smartbooker-dental-pro-trial-checklist.pdf"
                  className="inline-flex items-center gap-2 rounded-full bg-white text-slate-900 hover:bg-cyan-50 font-bold px-4 py-2 text-sm transition-colors"
                  data-testid="trial-checklist-download-pdf"
                >
                  <Download className="w-4 h-4" /> {language === "it" ? "Scarica PDF" : "Download PDF"}
                </a>
                <Button onClick={resetAll} variant="outline"
                        className="rounded-full bg-white/5 border-white/20 text-white hover:bg-white/10"
                        data-testid="trial-checklist-reset">
                  <RotateCcw className="w-4 h-4 mr-2" /> {language === "it" ? "Azzera" : "Reset"}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* SECTIONS */}
        <div className="space-y-4">
          {SECTIONS.map((sec, idx) => {
            const SectionIcon = sec.icon;
            const sectionTotal = sec.tasks.length;
            const sectionDone = sec.tasks.filter(t => checked[`${sec.id}.${t[0]}`]).length;
            const sectionPct = Math.round((sectionDone / sectionTotal) * 100);
            const allDone = sectionDone === sectionTotal;
            return (
              <div key={sec.id}
                   className={`rounded-2xl border bg-card overflow-hidden transition-all ${allDone ? "border-emerald-300 dark:border-emerald-800/60" : "border-slate-200 dark:border-white/10"}`}
                   data-testid={`trial-section-${sec.id}`}>
                {/* Section header */}
                <div className={`bg-gradient-to-r ${sec.color} text-white px-4 py-3 flex items-center gap-3`}>
                  <div className="w-9 h-9 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center shrink-0">
                    <SectionIcon className="w-4.5 h-4.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80">
                      {language === "it" ? "Area" : "Area"} {idx + 1} · {sec.day}
                    </p>
                    <h2 className="text-base font-black truncate">{sec.title}</h2>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs font-mono font-black">{sectionDone}/{sectionTotal}</p>
                    <p className="text-[9px] uppercase tracking-wider font-bold opacity-80">{sectionPct}%</p>
                  </div>
                </div>

                {/* Tasks table */}
                <table className="w-full text-sm" data-testid={`trial-section-${sec.id}-table`}>
                  <thead>
                    <tr className="border-b border-slate-100 dark:border-white/5 text-[9px] uppercase tracking-[0.14em] font-bold text-slate-400">
                      <th className="w-10 px-3 py-2"></th>
                      <th className="text-left px-2 py-2">{language === "it" ? "Task" : "Task"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sec.tasks.map(([taskId, name, hint, badge]) => {
                      const key = `${sec.id}.${taskId}`;
                      const done = !!checked[key];
                      return (
                        <tr key={taskId}
                            className={`border-b border-slate-100 dark:border-white/5 last:border-0 cursor-pointer transition-colors ${done ? "bg-emerald-50/40 dark:bg-emerald-950/15" : "hover:bg-slate-50 dark:hover:bg-white/2"}`}
                            onClick={() => toggle(sec.id, taskId)}
                            data-testid={`trial-task-${key}`}>
                          <td className="px-3 py-3 align-top w-10">
                            {done ? (
                              <CheckCircle2 className="w-5 h-5 text-emerald-500" data-testid={`trial-task-${key}-checked`} />
                            ) : (
                              <Circle className="w-5 h-5 text-slate-300 hover:text-slate-500" />
                            )}
                          </td>
                          <td className="px-2 py-3 align-top">
                            <p className={`font-bold text-[13px] ${done ? "text-emerald-700 dark:text-emerald-300 line-through decoration-emerald-400/60 decoration-1" : "text-slate-800 dark:text-slate-100"}`}>
                              {name}
                              {badge === "pro" && (
                                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[8px] uppercase tracking-wider font-black rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200 border border-violet-200 dark:border-violet-800/60">PRO</span>
                              )}
                              {badge === "ai" && (
                                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[8px] uppercase tracking-wider font-black rounded-full bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-200 border border-cyan-200 dark:border-cyan-800/60">AI</span>
                              )}
                            </p>
                            <p className={`text-[11.5px] mt-0.5 leading-snug ${done ? "text-slate-400 line-through" : "text-slate-500 dark:text-slate-400"}`}>
                              {hint}
                            </p>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        {progressPct === 100 && (
          <div className="rounded-3xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white p-6 shadow-xl text-center"
               data-testid="trial-checklist-complete-banner">
            <Trophy className="w-10 h-10 mx-auto mb-2" />
            <h2 className="text-xl font-black">{language === "it" ? "Trial completato! 🎉" : "Trial complete! 🎉"}</h2>
            <p className="text-sm text-white/90 max-w-md mx-auto mt-1">
              {language === "it"
                ? "Hai testato tutte le 40 funzioni · ora hai tutto per decidere se attivare Dental Pro."
                : "You've tested all 40 features · time to decide on Dental Pro."}
            </p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default DentalProTrialChecklist;
